<template>
  <vs-card v-if="showComponent" class="flex flex-col">
    <div slot="header">
      <h3>{{ title }}</h3>
    </div>

    <div>
      <div v-if="listArquivos.length == 0" class="p-5">
        <h6>Não há fotos ou documentos a serem exibidos.</h6>
      </div>
      <div
        v-else
        :class="[
          'flex flex-row gap-4 flex-wrap justify-between lg:justify-start',
          listArquivos.length > 2 && 'lg:justify-around',
        ]"
      >
        <div
          class="flex flex-row width-responsive"
          v-for="arquivo in listArquivos"
          :key="arquivo.idEventoFoto"
        >
          <vs-card
            :class="[
              'vs-card--content-custom flex w-full h-full md:w-120 md:h-112 relative',
              (arquivo.rejectionReason && 'bg-red text-white') ||
                (arquivo.approved && 'bg-success text-white') ||
                (arquivo.deletar && 'opacity-50'),
            ]"
          >
            <div class="flex">
              <div class="flex flex-row w-full justify-between">
                <div class="p-2 break-all w-full">
                  <p class="text-lg w-full">
                    {{
                      arquivo.nomeTipoArquivo
                        ? `Tipo: ${arquivo.nomeTipoArquivo}`
                        : null
                    }}
                  </p>
                  <p>{{ arquivo.legenda || arquivo.nome }}</p>
                  <p>{{ formatDateHour(arquivo.updatedAt) }}</p>
                  <p>{{ arquivo.nomeUsuario }}</p>
                </div>
                <div class="flex gap-2">
                  <vs-button
                    v-tooltip="'Download'"
                    type="gradient"
                    color="primary"
                    icon="file_download"
                    size="small"
                    :disabled="
                      downloadingFiles[arquivo.idEventoFoto] ||
                      isDownloadingAllFiles
                    "
                    @click="downloadArquivo(arquivo)"
                  ></vs-button>

                  <vs-button
                    v-if="arquivo.deletar"
                    v-tooltip="'Deletar'"
                    type="gradient"
                    color="success"
                    icon="undo"
                    size="small"
                    @click="undoArquivoToDelete(arquivo)"
                    :disabled="isDownloadingAllFiles"
                  ></vs-button>

                  <vs-button
                    v-else
                    v-tooltip="'Deletar'"
                    type="gradient"
                    color="danger"
                    icon="delete"
                    size="small"
                    @click="selectArquivoToDelete(arquivo)"
                    :disabled="isDownloadingAllFiles || arquivo.approved"
                  ></vs-button>
                </div>
              </div>
            </div>

            <div class="flex justify-center mb-4 w-full min-w-full">
              <div
                v-if="isPdf(arquivo.nome)"
                class="flex justify-center min-w-full max-h-full"
              >
                <a
                  class="flex justify-center w-full"
                  :href="arquivo.nome"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    class="object-contain h-64 w-full cursor-pointer"
                    src="../../../../../assets/images/elements/PDF_file_icon2.png"
                  />
                </a>
              </div>
              <div
                v-else-if="isVideo(arquivo.nome)"
                class="flex justify-center min-w-full max-h-full"
              >
                <a
                  class="flex justify-center"
                  :href="arquivo.nome"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    class="object-contain h-64 w-full cursor-pointer"
                    src="../../../../../assets/images/elements/video_file_icon.png"
                  />
                </a>
              </div>
              <div
                v-else-if="isAudio(arquivo.nome)"
                class="flex justify-center min-w-full max-h-full"
              >
                <a
                  class="flex justify-center w-full"
                  :href="arquivo.nome"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <audio
                    class="object-contain h-64 w-full cursor-pointer"
                    :src="arquivo.nome"
                    controls
                    muted
                    loop
                  />
                </a>
              </div>
              <div v-else class="flex justify-center w-full max-h-full">
                <img
                  @click="openModalDetalhesFoto(arquivo)"
                  class="object-contain h-64 w-full cursor-pointer"
                  :src="arquivo.nome"
                />
              </div>
            </div>

            <div
              class="flex flex-row justify-end self-end gap-2 p-3 absolute right-0 bottom-0 mr-2"
              v-if="
                !arquivo.approved &&
                !arquivo.rejectionReason &&
                $can('Pode Aprovar e Reprovar Fotos', 'Eventos')
              "
            >
              <vs-button
                v-tooltip="'Reprovar'"
                type="gradient"
                color="danger"
                icon="thumb_down"
                @click="rejectFile(arquivo)"
                :disabled="isProcessingApproveOrRejectionFile"
              >
              </vs-button>

              <vs-button
                v-tooltip="'Aprovar'"
                type="gradient"
                color="success"
                icon="thumb_up"
                @click="approveFile(arquivo)"
                :disabled="isProcessingApproveOrRejectionFile"
              >
              </vs-button>
            </div>
            <div
              v-if="arquivo.rejectionReason"
              class="flex flex-row justify-end self-end gap-2 pt-2 absolute bottom-0 left-0 p-3"
            >
              <span class="text-base">{{ arquivo.rejectionReason }}</span>
            </div>
          </vs-card>
        </div>
      </div>

      <div v-if="listArquivos.length >= filesCapacityLimit">
        <vs-alert title="Atenção" active="true" color="danger">
          Você chegou ao limite máximo de {{ filesCapacityLimit }} fotos. Favor
          remover fotos atuais para poder acrescentar novas fotos.
        </vs-alert>
      </div>

      <div class="flex justify-between items-center mt-5 lg:flex-row flex-col">
        <div class="p-5">
          <span class="colorBlue">
            Limite de fotos: {{ listArquivos.length }} /
            {{ filesCapacityLimit }}
          </span>
        </div>
        <div class="flex gap-3 lg:flex-row flex-col">
          <vs-button
            color="danger"
            :disabled="!listArquivosToDelete.length"
            @click="deleteRegistroArquivos()"
          >
            <div class="flex items-center gap-2 justify-center">
              <feather-icon icon="Trash2Icon" />
              <span
                >Deletar: {{ listArquivosToDelete.length }} arquivos
                selecionados</span
              >
            </div>
          </vs-button>

          <vs-button
            color="primary"
            v-if="
              showInformarSegurado &&
              $can('Pode Aprovar e Reprovar Fotos', 'Eventos')
            "
            @click="openModalNotifyInsured"
            :disabled="isProcessingApproveOrRejectionFile"
          >
            Informar Segurado
          </vs-button>

          <vs-button
            color="primary"
            :disabled="!listArquivos.length || isDownloadingAllFiles"
            @click="downloadAllFiles"
          >
            <div class="flex items-center gap-2">
              <feather-icon icon="DownloadIcon" />
              <span v-if="isDownloadingAllFiles">Baixando fotos...</span>
              <span v-else>Baixar Fotos</span>
            </div>
          </vs-button>

          <vs-button
            @click="openModalUpload(idReferente)"
            :disabled="listArquivos.length >= filesCapacityLimit"
          >
            <div class="flex items-center gap-2">
              <feather-icon icon="UploadIcon" />Upload
            </div>
          </vs-button>

          <vs-button
            v-if="
              showOpeningProcessButton &&
              $can('Pode Liberar Abertura do Item', 'Eventos')
            "
            color="success"
            @click="handleOpeningProcessEvent"
            :disabled="isOpeningProcess"
          >
            Liberar Abertura do Item
          </vs-button>
        </div>
      </div>

      <vs-popup
        title="Fotos e Documentos"
        :active.sync="modalFotoAtivo"
        fullscreen
      >
        <div v-if="itemModalSelecionado">
          <div class="flex justify-center m-5 max-w-full">
            <h4 class="truncate text-ellipsis">
              {{
                itemModalSelecionado.legenda
                  ? itemModalSelecionado.legenda
                  : itemModalSelecionado.nome
              }}
            </h4>
          </div>
          <div class="flex md:m-4 justify-between mb-10 lg:justify-center">
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="2"
              class="sm:p-2 p-4 gap-x-1 z-40"
            >
              <vs-button
                icon-pack="feather"
                icon="icon-arrow-left"
                color="warning"
                class="z-40 sm:p-5"
                @click="mudaFoto('anterior')"
              />
            </vs-col>
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="3"
              class="sm:p-2 gap-x-1 z-40"
            >
              <vs-button
                icon-pack="feather"
                icon="icon-rotate-ccw"
                color="primary"
                class="z-40"
                @click="rotateLeft"
              />
              <div
                class="z-40 bg-white px-3 rounded flex flex-col items-center"
              >
                <span>Zoom</span>
                <vs-switch
                  @change="toggleZoom"
                  color="success"
                  v-model="zoomFotoSwitch"
                >
                  <span slot="on">Sim</span>
                  <span slot="off">Não</span>
                </vs-switch>
              </div>
              <vs-button
                icon-pack="feather"
                icon="icon-rotate-cw"
                color="primary"
                class="z-40"
                @click="rotateRight"
              />
            </vs-col>
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="2"
              class="sm:p-2 p-4 gap-x-1 z-40"
            >
              <vs-button
                icon-pack="feather"
                icon="icon-arrow-right"
                color="warning"
                class="z-40"
                @click="mudaFoto('próximo')"
              />
            </vs-col>
          </div>
          <div
            :class="[
              'w-full h-full flex justify-center ',
              rotationAngle !== 0 ||
                (zoomFotoSwitch &&
                  `overflow-scroll w-[${this.containerSize}]] h-[${this.containerSize}]`),
            ]"
          >
            <div
              :class="[
                'flex flex-col object-contain pb-3 lg:p-4 justify-center content-center',
                zoomFotoSwitch
                  ? `w-[${this.containerSize}] h-[${this.containerSize}]`
                  : '',
                rotationAngle > 0 && 'w-full',
              ]"
              :style="{
                maxWidth: containerSize,
                maxHeight: containerSize,
                overflow: 'auto',
              }"
            >
              <img
                :class="[
                  `${
                    zoomFotoSwitch ? 'object-contain' : 'object-cover'
                  } transition-transform duration-200`,
                ]"
                :style="{
                  transform: 'rotate(' + rotationAngle + 'deg)',
                  transformOrigin: 'center center',
                  width: containerSize,
                  height: containerSize,
                  overflow: 'scroll',
                }"
                :src="itemModalSelecionado.nome"
              />
            </div>
          </div>
          <div slot="footer" class="bg-red-500">
            <vs-row class="flex justify-end gap-1">
              <vs-button
                v-tooltip="'Recusar Foto'"
                type="gradient"
                color="danger"
                icon="thumb_down"
              ></vs-button>
              <vs-button
                v-tooltip="'Aprovar Foto'"
                type="gradient"
                color="success"
                icon="thumb_up"
              ></vs-button>
            </vs-row>
          </div>
        </div>
      </vs-popup>

      <vs-popup
        :title="this.titleModalUpload"
        :active.sync="modalUploadAtivo"
        :id="`idReferente${idReferente}`"
      >
        <div v-if="!listArquivosToUpload.length" class="m-5 mb-10">
          <h5>Não há arquivos selecionados</h5>
        </div>
        <div v-else class="flex flex-wrap">
          <div
            v-for="(arquivo, index) in listArquivosToUpload"
            :key="index"
            class="lg:w-1/2 w-full p-5"
          >
            <vs-card>
              <div class="flex justify-end">
                <vs-button
                  v-tooltip="'Deletar arquivo'"
                  type="gradient"
                  color="danger"
                  icon="close"
                  size="small"
                  class="mb-2"
                  @click="deleteArquivoUpload(index)"
                ></vs-button>
              </div>

              <div class="flex justify-center">
                <img
                  v-if="isPdf(arquivo.nome)"
                  class="w-1/2"
                  src="../../../../../assets/images/elements/PDF_file_icon2.png"
                />
                <video
                  v-if="isVideo(arquivo.nome)"
                  class="w-full"
                  :src="arquivo.url"
                  controls
                  muted
                  loop
                ></video>
                <audio
                  v-else-if="isAudio(arquivo.nome)"
                  class="w-1/2"
                  :src="arquivo.url"
                  controls
                  muted
                  loop
                />
                <img v-else class="w-full" :src="arquivo.url" />
              </div>
              <div class="mt-2">
                <vs-input
                  label="Legenda do arquivo:"
                  class="w-full"
                  :value="arquivo.legenda"
                  @change="
                    updateArquivo(arquivo, 'legenda', $event.target.value)
                  "
                ></vs-input>
              </div>
              <div class="mt-2">
                <label class="vs-select--label">Tipo de Arquivo:</label>
                <v-select
                  :options="listTiposArquivos"
                  label="description"
                  :reduce="(item) => item.archiveTypeId"
                  v-model="listIdsTipoArquivoSelecionados[index]"
                  @input="
                    updateArquivo(
                      arquivo,
                      'idTipoArquivo',
                      listIdsTipoArquivoSelecionados[index],
                    )
                  "
                ></v-select>
              </div>
            </vs-card>
          </div>
        </div>
        <div
          v-if="
            listArquivosToUpload.length + listArquivos.length >=
            filesCapacityLimit
          "
          class="mb-3"
        >
          <vs-alert title="Atenção" active="true" color="danger">
            <span v-if="listArquivos.length">
              Você já possui {{ listArquivos.length }} fotos salvas.
            </span>
            Subindo essas fotos, você chegará ao limite total máximo de
            {{ filesCapacityLimit }}
            fotos. Favor remover fotos atuais para poder acrescentar novas
            fotos.
          </vs-alert>
        </div>

        <div class="flex justify-end gap-5">
          <vs-button
            @click="openEscolhaArquivoUpload(idReferente)"
            :disabled="
              listArquivosToUpload.length + listArquivos.length >=
              filesCapacityLimit
            "
            color="warning"
          >
            <div class="flex items-center gap-2">
              <feather-icon icon="PlusIcon" />Escolher arquivos
            </div>
          </vs-button>
          <input
            :id="`fileUpload${idReferente}${idTerceiro}`"
            type="file"
            accept="image/*, video/*, audio/*,.pdf"
            @change="selectArquivoUpload"
            name="files"
            hidden
            multiple
            enctype="multipart/form-data"
          />

          <vs-button
            @click="uploadArquivosSelecionados()"
            :disabled="
              listArquivosToUpload.length + listArquivos.length >
                filesCapacityLimit || !listArquivosToUpload.length
            "
          >
            <div class="flex items-center gap-2">
              <feather-icon icon="UploadIcon" />Upload:
              {{ listArquivosToUpload.length }} arquivos selecionados
            </div>
          </vs-button>
        </div>
      </vs-popup>

      <vs-popup
        title="Informar Segurado"
        :active.sync="modalNotifyInsured"
        @close="modalNotifyInsured = false"
      >
        <div v-if="rejectFiles.length" class="mt-4">
          <p v-html="notifyInsuredMessage"></p>
        </div>

        <p v-else class="mt-4">Nenhum arquivo foi rejeitado.</p>

        <div class="flex justify-end gap-3 mt-5">
          <vs-button color="dark" @click="sendWhatsAppNotification">
            Enviar
          </vs-button>
        </div>
      </vs-popup>

      <vs-popup
        :title="rejectionTitle"
        @cancel="rejectionReason = null"
        @close="isPromptActive = false"
        :active.sync="isPromptActive"
      >
        <div class="flex flex-col h-full">
          <vs-textarea
            placeholder="Detalhe motivo da rejeição da foto."
            v-model="rejectionReason"
            class="flex-grow"
          />

          <vs-button
            @click="submitRejection()"
            color="danger"
            class="mb-2 my-5 mt-auto"
          >
            <span>Reprovar</span>
          </vs-button>
        </div>
      </vs-popup>

      <vs-popup
        :title="approveTitle"
        :active.sync="isNotifyInsuredModalOpen"
        @cancel="selectedFile = null"
        @close="isNotifyInsuredModalOpen = false"
      >
        <p>Tem certeza que deseja aprovar este arquivo?</p>

        <div class="flex justify-end gap-3 mt-5">
          <vs-button color="danger" @click="isNotifyInsuredModalOpen = false">
            Não
          </vs-button>

          <vs-button color="success" @click="handleNotifyInsured">
            Sim
          </vs-button>
        </div>
      </vs-popup>
    </div>
  </vs-card>
</template>

<script>
import jwt from 'jsonwebtoken';
import moment from 'moment';
import serviceEventos from '../service/index';
import vSelect from 'vue-select';

import service from '../../../../../services';
import notify from './../../../../../utils/notify';
import { standardMessageEvents } from '../standard-message-events';
import services from '../../../../../services';

export default {
  props: {
    formData: Object,
    arquivos: Array,
    idReferente: Number,
    title: {
      type: String,
      default: 'Cadastrar Fotos e Documentos',
    },
  },
  components: { vSelect },
  data() {
    return {
      showComponent: true,
      idEvento: null,
      idTerceiro: null,
      idUsuario: null,
      idEmpresa: null,
      modalFotoAtivo: false,
      modalUploadAtivo: false,
      titleModalUpload: null,
      itemModalSelecionado: null,
      listArquivosToUpload: [],
      listArquivosToDelete: [],
      listArquivos: [],
      listTiposArquivos: [],
      filesCapacityLimit: 300,
      files: [],
      itemModalIndex: 0,
      rotationAngle: 0,
      zoomFotoSwitch: false,
      listIdsTipoArquivoSelecionados: [],
      isDownloadingAllFiles: false,
      downloadingFiles: {},
      selectedFile: null,
      rejectionTitle: 'Motivo de Reprovação',
      rejectionReason: null,
      isPromptActive: false,
      isNotifyInsuredModalOpen: false,
      modalNotifyInsured: false,
      isProcessingApproveOrRejectionFile: false,
      isOpeningProcess: false,
      encriptedEventId: null,
      protocol: null,
      sizeWidthScreen: 0,
    };
  },
  watch: {
    formData(value) {
      this.idEvento = value.idEvento;
      this.idTerceiro = value.idTerceiro || null;
    },
    arquivos(value) {
      this.listArquivos = value;
    },
  },
  computed: {
    normalizedAngle() {
      return ((this.rotationAngle % 360) + 360) % 360;
    },
    containerSize() {
      return this.handleSizeContainerDetailsImages(this.normalizedAngle);
    },
    approveTitle() {
      return this.selectedFile
        ? `Aprovar ${this.selectedFile.nomeTipoArquivo || 'Arquivo'}`
        : 'Confirmar Aprovação';
    },
    rejectFiles() {
      return this.listArquivos.filter((file) => file.rejectionReason);
    },
    filteredFilesWithType() {
      return (this.listArquivos || []).filter((file) => file.idTipoArquivo);
    },
    showInformarSegurado() {
      return (
        this.filteredFilesWithType.length &&
        this.filteredFilesWithType.every(
          (file) => file.approved || file.rejectionReason,
        ) &&
        this.filteredFilesWithType.some((file) => file.rejectionReason)
      );
    },
    showOpeningProcessButton() {
      const hasOpeningProcessDate =
        this.formData &&
        this.formData.processosDatas &&
        this.formData.processosDatas.aberturaProcesso;

      return (
        this.filteredFilesWithType.length &&
        this.filteredFilesWithType.every((file) => file.approved) &&
        !hasOpeningProcessDate
      );
    },
    notifyInsuredMessage() {
      const rejectedFilesList = this.generateRejectedFilesList();

      if (!rejectedFilesList.length) {
        return `
  ${standardMessageEvents.notifyInsured.header}<br /><br />
  ${standardMessageEvents.notifyInsured.processCompleted}<br /><br />
  Nenhum arquivo foi rejeitado.<br /><br />
  ${standardMessageEvents.notifyInsured.doubtsContact}<br /><br />
  ${standardMessageEvents.notifyInsured.closing}`;
      }

      const rejectedFiles =
        standardMessageEvents.htmlFormatted.rejectedFilesList(
          rejectedFilesList,
        );

      return `
  ${standardMessageEvents.notifyInsured.header}<br /><br />
  ${standardMessageEvents.notifyInsured.processCompleted}<br /><br />
  ${standardMessageEvents.notifyInsured.rejectedFilesTitle}<br /><br />
  ${rejectedFiles}<br />
  ${standardMessageEvents.htmlFormatted.protocolText(this.protocol)}<br />
  ${standardMessageEvents.htmlFormatted.generateUrl(
    this.encriptedEventId,
    this.idTerceiro,
  )}<br />
  ${standardMessageEvents.notifyInsured.doubtsContact}<br /><br />
  ${standardMessageEvents.notifyInsured.closing}`;
    },
  },
  methods: {
    handleSizeContainerDetailsImages(normalizedAngle) {
      const isRotated = normalizedAngle % 180 === 90;
      const isZoomed = this.zoomFotoSwitch;
      const isWideScreen = this.sizeWidthScreen > 800;

      const zoomedSize = isWideScreen && isZoomed ? '202rem' : '30rem';
      const defaultSize = '80%';

      return isRotated || isZoomed ? zoomedSize : defaultSize;
    },
    openModalDetalhesFoto(arquivo) {
      this.modalFotoAtivo = true;
      this.itemModalSelecionado = arquivo;
    },
    async openModalUpload(idReferente) {
      if (!this.listTiposArquivos.length) {
        await this.buscaTiposArquivos();
      }

      this.modalUploadAtivo = true;
      this.titleModalUpload = 'Upload de Fotos e Documentos';

      switch (idReferente) {
        case 1:
          this.titleModalUpload += ' - Dados Gerais';
          break;

        case 2:
          this.titleModalUpload += ' - Reparo do Veículo';
          break;

        case 3:
          this.titleModalUpload += ' - Terceiros';
          break;
      }
    },
    openEscolhaArquivoUpload() {
      document
        .getElementById(`fileUpload${this.idReferente}${this.idTerceiro}`)
        .click();
    },
    selectArquivoUpload(event) {
      const fileList = event.target.files;

      if (!fileList.length) {
        return;
      }

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        this.listArquivosToUpload.push({
          legenda: this.getNomeArquivoSemExtensao(file.name),
          url: URL.createObjectURL(file),
          nome: file.name,
        });
        this.files.push(file);
      }
    },
    getNomeArquivoSemExtensao(nomeArquivo) {
      const ultimoPontoIndex = nomeArquivo.lastIndexOf('.');

      if (ultimoPontoIndex !== -1) {
        return nomeArquivo.substring(0, ultimoPontoIndex);
      }

      return nomeArquivo;
    },
    isPdf(urlArquivo) {
      const ext = urlArquivo.split('.').pop();

      return ext === 'pdf' ? true : false;
    },
    isVideo(urlArquivo) {
      const ext = urlArquivo.split('.').pop().toLowerCase();
      const videoExtensions = [
        'mp4',
        'm4v',
        'avi',
        'mkv',
        'mov',
        'wmv',
        'flv',
        'webm',
        'mpeg',
        'quicktime',
      ];

      return videoExtensions.includes(ext);
    },
    isAudio(urlArquivo) {
      const ext = urlArquivo.split('.').pop().toLowerCase();
      const audioExtensions = [
        'mp3',
        'wav',
        'ogg',
        'flac',
        'aac',
        'm4a',
        'wma',
      ];

      return audioExtensions.includes(ext);
    },
    updateArquivo(arquivo, propriedade, valor) {
      if (arquivo && propriedade && valor) arquivo[propriedade] = valor;
    },
    deleteArquivoUpload(index) {
      this.listArquivosToUpload.splice(index, 1);
      this.listIdsTipoArquivoSelecionados.splice(index, 1);

      const deletedFile = this.listArquivosToUpload[index];
      const fileIndex = this.files.findIndex(
        (file) => file.name === deletedFile.nome,
      );

      if (fileIndex !== -1) {
        this.files.splice(fileIndex, 1);
      }
    },
    selectArquivoToDelete(arquivoSelecionado) {
      arquivoSelecionado.deletar = true;
      this.listArquivosToDelete.push(arquivoSelecionado);
    },
    undoArquivoToDelete(arquivoSelecionado) {
      delete arquivoSelecionado.deletar;

      const index = this.listArquivosToDelete.findIndex(
        (item) => item.idArquivo === arquivoSelecionado.idArquivo,
      );

      if (index !== -1) {
        this.listArquivosToDelete.splice(index, 1);
      }
    },
    async registraArquivosUpload(files, listArquivosToUpload) {
      try {
        const listFiles = [];

        files.forEach((file, index) => {
          listFiles.push({
            idEvento: this.idEvento,
            idTerceiro: this.idTerceiro,
            idFotoReferente: this.idReferente,
            nome: file,
            legenda: listArquivosToUpload[index].legenda,
            uploadBy: this.idUsuario,
            idTipoArquivo: listArquivosToUpload[index].idTipoArquivo,
          });
        });

        const result = await service.post(
          'eventos2',
          'registrar-fotos-documentos',
          listFiles,
        );

        // POG
        setTimeout(() => {
          this.listArquivos = result.data;
        }, 500);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async uploadArquivosSelecionados() {
      try {
        this.$vs.loading();

        const formDataSend = new FormData();

        this.files.forEach((file) => {
          formDataSend.append('files', file);
        });
        const { uploadedFiles } = await service.uploadAll(
          'upload-multi',
          'Eventos/' + this.idEvento,
          formDataSend,
        );

        await this.registraArquivosUpload(
          uploadedFiles,
          this.listArquivosToUpload,
        );

        this.modalUploadAtivo = false;
        this.listArquivosToUpload = [];
        this.files = [];
        this.listIdsTipoArquivoSelecionados = [];
      } catch (error) {
        notify.notifyFail(this.$vs, 'Não foi possível subir os arquivos');
        console.log(error);
      } finally {
        this.$vs.loading.close();
      }
    },
    getDecoded() {
      const token = localStorage.getItem('user-token');

      if (token) {
        const decoded = jwt.decode(token);
        this.idUsuario = decoded.id_usuario;
        this.idEmpresa = decoded.id_empresa;
      }
    },
    async deleteRegistroArquivos() {
      try {
        this.$vs.loading();

        const objSend = {
          idEvento: this.idEvento,
          idTerceiro: this.idTerceiro,
          idFotoReferente: this.idReferente,
          ids: [],
        };

        this.listArquivosToDelete.forEach((file) => {
          if (file.deletar) {
            objSend.ids.push(file.idEventoFoto);
          }
        });

        const result = await service.post(
          'eventos2',
          'deletar-fotos-documentos',
          objSend,
        );

        this.listArquivos = result.data;
      } catch (error) {
        notify.notifyFail(this.$vs, 'Não foi possível deletar os arquivos');
        console.log(error);
      } finally {
        this.listArquivosToDelete = [];
        this.$vs.loading.close();
      }
    },
    formatDateHour(value) {
      if (!value) {
        return '';
      }

      return moment(value).format('DD/MM/YYYY HH:mm');
    },
    mudaFoto(param) {
      const currentIndex = this.arquivos.findIndex(
        (image) =>
          image.idEventoFoto === this.itemModalSelecionado.idEventoFoto,
      );

      const updatedIndex =
        param === 'próximo'
          ? (currentIndex + 1) % this.arquivos.length
          : param === 'anterior'
          ? (currentIndex - 1 + this.arquivos.length) % this.arquivos.length
          : currentIndex;

      this.itemModalSelecionado = this.arquivos[updatedIndex];
      this.openModalDetalhesFoto(this.itemModalSelecionado);
    },
    rotateLeft() {
      this.rotationAngle = (this.rotationAngle - 90 + 360) % 360;
    },
    rotateRight() {
      this.rotationAngle = (this.rotationAngle + 90) % 360;
    },
    toggleZoom() {
      this.zoomFotoSwitch = !this.zoomFotoSwitch;
    },
    async buscaTiposArquivos() {
      try {
        this.$vs.loading();
        const resultTipoArquivo = await serviceEventos.getListTipoArquivoEvento(
          this.idEmpresa,
        );
        this.listTiposArquivos = resultTipoArquivo.data;
      } catch (error) {
        console.error(error);
        notify.notifyFail(
          this.$vs,
          'Não foi possível buscar os tipos de arquivos do sinistro.',
        );
      } finally {
        this.$vs.loading.close();
      }
    },
    async downloadArquivo(arquivo, showNotify = true) {
      if (this.downloadingFiles[arquivo.idEventoFoto]) return;

      this.$set(this.downloadingFiles, arquivo.idEventoFoto, true);

      try {
        const imageUrl = arquivo.nome;
        const encodedUrl = encodeURIComponent(imageUrl);

        const response = await service.getBlob('eventos2', {
          path: `downloadArquivo/${encodedUrl}`,
          responseType: 'blob',
        });

        const element = document.createElement('a');
        element.href = URL.createObjectURL(response);
        element.setAttribute('download', imageUrl.split('/').pop());

        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        if (showNotify) {
          notify.notifySuccess(this.$vs, 'Arquivo baixado com sucesso.');
        }
      } catch (error) {
        console.error('Erro ao baixar o arquivo:', error);
        notify.notifyFail(this.$vs, 'Erro ao baixar o arquivo.');
      } finally {
        this.$set(this.downloadingFiles, arquivo.idEventoFoto, false);
      }
    },
    async getPhotoOfBeneficiaryByTypeFileName(typeFileName) {
      if (!typeFileName) {
        return null;
      }

      try {
        const response = await services.get(
          'eventos2',
          `getPhotoOfBeneficiaryByTypeFileName/${this.idEvento}/${typeFileName}`,
        );
        if (response && response.fileName) {
          return response.fileName;
        }

        return null;
      } catch (e) {
        notify.notifyFail(this.$vs, 'Erro ao buscar foto do segurado.', e);
        return null;
      }
    },
    async downloadAllFiles() {
      if (!this.listArquivos.length) {
        notify.notifyFail(this.$vs, 'Não há fotos para baixar.');
        return;
      }

      this.isDownloadingAllFiles = true;
      const cnhBeneficiaryDocument = this.idTerceiro
        ? await this.getPhotoOfBeneficiaryByTypeFileName('CNH')
        : null;

      try {
        const links = [
          ...this.listArquivos.map((arquivo) => arquivo.nome),
          ...(this.formData.linkCertificadoAberturaEvento
            ? [this.formData.linkCertificadoAberturaEvento]
            : []),
          ...(cnhBeneficiaryDocument ? [cnhBeneficiaryDocument] : []),
        ];

        if (!links.length) {
          notify.notifyFail(
            this.$vs,
            'Nenhum arquivo disponível para download.',
          );
          return;
        }

        const response = await service.getBlob('eventos2', {
          path: `downloadMultipleFiles`,
          method: 'POST',
          data: { links },
          responseType: 'blob',
        });

        const fileName =
          (this.idTerceiro
            ? this.formData.dadosVeiculoTerceiro &&
              (this.formData.dadosVeiculoTerceiro.placa ||
                this.formData.dadosVeiculoTerceiro.chassi)
            : this.formData.dadosVeiculo &&
              (this.formData.dadosVeiculo.placa ||
                this.formData.dadosVeiculo.chassi)) || 'arquivos';

        const beneficiaryName =
          (this.idTerceiro
            ? this.formData.dadosTerceiro && this.formData.dadosTerceiro.nome
            : this.formData.dadosCliente && this.formData.dadosCliente.nome) ||
          'beneficiario';

        const companyId = this.idEmpresa || 'empresa';

        const formattedFileName = `${fileName}-${beneficiaryName}-${companyId}`
          .replace(/\s+/g, '_')
          .replace(/[^a-zA-Z0-9-_]/g, '');

        const element = document.createElement('a');
        element.href = URL.createObjectURL(response);
        element.setAttribute('download', `${formattedFileName}.zip`);

        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        notify.notifySuccess(
          this.$vs,
          'Todos os arquivos foram baixados com sucesso.',
        );
      } catch (error) {
        console.error('Erro ao baixar os arquivos:', error);
        notify.notifyFail(this.$vs, 'Erro ao baixar todos os arquivos.');
      } finally {
        this.isDownloadingAllFiles = false;
      }
    },
    async approveOrRejectFile(eventPhotoId, approved, rejectionReason = null) {
      this.isProcessingApproveOrRejectionFile = true;
      try {
        const payload = { eventPhotoId, approved, rejectionReason };

        const result = await service.put(
          'eventos2',
          `approve-reject-file`,
          payload,
        );

        this.listArquivos = this.listArquivos.map((file) =>
          file.idEventoFoto === eventPhotoId
            ? { ...file, approved, rejectionReason: rejectionReason || null }
            : file,
        );

        notify.notifySuccess(
          this.$vs,
          approved ? 'Arquivo aprovado.' : 'Arquivo reprovado.',
        );

        if (!approved) {
          this.isPromptActive = false;
          this.rejectionReason = null;
        }

        return result.data;
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao processar arquivo.');
        console.error(error);
      } finally {
        this.isProcessingApproveOrRejectionFile = false;
      }
    },
    approveFile(file) {
      this.selectedFile = file;
      this.isNotifyInsuredModalOpen = true;
    },
    handleNotifyInsured() {
      if (!this.selectedFile) return;

      this.approveOrRejectFile(this.selectedFile.idEventoFoto, true);
      this.isNotifyInsuredModalOpen = false;
      this.selectedFile = null;
    },
    rejectFile(file) {
      this.selectedFile = file;
      this.rejectionReason = null;
      this.rejectionTitle = file.nomeTipoArquivo
        ? `Reprovar ${file.nomeTipoArquivo}`
        : 'Motivo de Reprovação';
      this.isPromptActive = true;
    },
    submitRejection() {
      if (!this.rejectionReason || this.rejectionReason.length < 10) {
        notify.notifyFail(
          this.$vs,
          'O motivo da rejeição deve ter pelo menos 10 caracteres.',
        );
        return;
      }
      this.approveOrRejectFile(
        this.selectedFile.idEventoFoto,
        false,
        this.rejectionReason,
      );
    },
    async openModalNotifyInsured() {
      this.modalNotifyInsured = false;

      try {
        const encryptedId = await this.generateEncryptedEventId();

        if (!encryptedId) {
          throw new Error('ID encriptado retornou inválido.');
        }

        this.protocol = this.formData.protocolo;
        this.encriptedEventId = encryptedId;

        this.modalNotifyInsured = true;
      } catch (error) {
        console.error('Erro ao obter o ID encriptado:', error);
        notify.notifyFail(this.$vs, 'Erro ao gerar o link do evento.');
      }
    },
    sendWhatsAppNotification() {
      const rawPhoneNumber = this.idTerceiro
        ? this.formData &&
          this.formData.dadosTerceiro &&
          this.formData.dadosTerceiro.contato1
        : this.formData &&
          this.formData.dadosCliente &&
          this.formData.dadosCliente.celular;

      const formattedPhoneNumber = this.formatPhoneNumber(rawPhoneNumber);

      if (!formattedPhoneNumber) {
        notify.notifyFail(
          this.$vs,
          'Número de celular inválido ou não encontrado.',
        );
        return;
      }

      const encodedMessage = encodeURIComponent(
        this.formatMessageForWhatsApp(this.notifyInsuredMessage),
      );

      const whatsappLink = `https://wa.me/${formattedPhoneNumber}?text=${encodedMessage}`;

      this.modalNotifyInsured = false;
      window.open(whatsappLink, '_blank');
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return null;

      const cleanedNumber = phoneNumber.replace(/\D/g, '');

      if (cleanedNumber.startsWith('55') && cleanedNumber.length >= 12) {
        return cleanedNumber;
      }

      if (cleanedNumber.length === 11 || cleanedNumber.length === 10) {
        return `55${cleanedNumber}`;
      }

      return null;
    },
    formatMessageForWhatsApp() {
      const rejectedFilesList = this.generateRejectedFilesList();

      if (rejectedFilesList.length === 0) {
        return `${standardMessageEvents.notifyInsured.header}
${standardMessageEvents.notifyInsured.processCompleted}
Nenhum arquivo foi rejeitado.
${standardMessageEvents.notifyInsured.doubtsContact}
${standardMessageEvents.notifyInsured.closing}`;
      }

      const rejectedFiles =
        standardMessageEvents.whatsapp.fileRejectedList(rejectedFilesList);

      return `${standardMessageEvents.notifyInsured.header}
${standardMessageEvents.notifyInsured.processCompleted}
${standardMessageEvents.notifyInsured.rejectedFilesTitle}
${rejectedFiles}
${standardMessageEvents.whatsapp.protocolText(this.protocol)}
${standardMessageEvents.whatsapp.generateUrl(
  this.encriptedEventId,
  this.idTerceiro,
)}
${standardMessageEvents.notifyInsured.doubtsContact}
${standardMessageEvents.notifyInsured.closing}`;
    },
    generateRejectedFilesList() {
      if (!Array.isArray(this.rejectFiles) || !this.rejectFiles.length) {
        return [];
      }

      return this.rejectFiles
        .filter((file) => file && file.nomeTipoArquivo && file.rejectionReason)
        .map((file) => ({
          title: file.nomeTipoArquivo.trim(),
          reason: file.rejectionReason.trim(),
        }));
    },
    async generateEncryptedEventId() {
      try {
        const eventId = this.idTerceiro ? this.idTerceiro : this.idEvento;

        if (!eventId) {
          notify.notifyFail(
            this.$vs,
            'O ID do evento é necessário para criptografia.',
          );
          return;
        }

        const payload = { eventId };

        const result = await service.post(
          'eventos2',
          `encrypt-event-id`,
          payload,
        );

        return result.data.encryptedId;
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao criptografar o ID do evento.');
        console.error('Encryption error:', error);
      }
    },
    async handleOpeningProcessEvent() {
      this.isOpeningProcess = true;
      try {
        const eventId = this.idEvento;
        const thirdPersonId = this.idTerceiro;
        const payload = { eventId, thirdPersonId };

        const result = await service.put(
          'eventos2',
          `opening-process-event`,
          payload,
        );

        notify.notifySuccess(this.$vs, result.data.message);
        window.location.reload();

        return result.data;
      } catch (error) {
        notify.notifyFail(this.$vs, error);
      } finally {
        this.isOpeningProcess = false;
      }
    },
    updateSize() {
      this.sizeWidthScreen = window.innerWidth;
    },
  },
  mounted() {
    this.getDecoded();

    this.idEvento = this.formData.idEvento;
    this.idTerceiro = this.formData.idTerceiro || null;
    this.listArquivos = this.arquivos;

    this.updateSize();
    window.addEventListener('resize', this.updateSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSize);
  },
};
</script>

<style>
.colorBlue {
  color: #00bfed;
}

.vs-popup--content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.vs-card--content-custom .vs-card--content {
  width: 100%;
}

.remove {
  background-color: rgb(252 165 165);
  color: black;
}

@media (max-width: 1024px) {
  .width-responsive {
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .vs-card--content-custom {
    min-width: 100%;
    min-height: 100%;
    width: 100%;
  }
}
</style>
